"use client";

import { usePathname, useRouter } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useTransition,
} from "react";

import { authApi, useGetProfileQuery } from "@/feature/auth/api/auth-api";
import { toLoginRedirect } from "@/feature/auth/lib/auth-redirect";
import {
  clearPersistedAuthSession,
  getAuthSyncSourceId,
  persistAuthSession,
  readPersistedAuthSession,
  subscribeToAuthSync,
} from "@/feature/auth/lib/auth-session";
import {
  hydrateFromStorage,
  logout,
  selectAccessToken,
  selectAuthUser,
  selectIsAuthenticated,
  selectIsAuthHydrated,
  setSession,
} from "@/feature/auth/state/auth-slice";
import type { AuthUser } from "@/feature/auth/types/auth.types";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { Spinner } from "../loader/spinner";

type AuthContextValue = {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  isAuthTransitionPending: boolean;
  navigateWithTransition: (path: string, method?: "push" | "replace") => void;
  completeLogin: (redirectTo: string) => Promise<boolean>;
  logoutAndRedirect: (redirectTo?: string) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isDashboardRoute(pathname: string) {
  return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const accessToken = useAppSelector(selectAccessToken);
  const user = useAppSelector(selectAuthUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isHydrated = useAppSelector(selectIsAuthHydrated);
  const [isAuthTransitionPending, startAuthTransition] = useTransition();

  const navigateWithTransition = useCallback(
    (path: string, method: "push" | "replace" = "push") => {
      startAuthTransition(() => {
        if (method === "replace") {
          router.replace(path);
          return;
        }
        router.push(path);
      });
    },
    [router, startAuthTransition],
  );

  const completeLogin = useCallback(
    async (redirectTo: string) => {
      const isPersisted = await persistAuthSession();
      if (!isPersisted) {
        dispatch(logout());
        dispatch(baseApi.util.resetApiState());
        return false;
      }

      const session = await readPersistedAuthSession();
      if (!session) {
        dispatch(logout());
        dispatch(baseApi.util.resetApiState());
        return false;
      }

      dispatch(setSession(session));
      dispatch(authApi.util.invalidateTags(["AuthProfile"]));
      void dispatch(
        authApi.endpoints.getProfile.initiate(undefined, {
          forceRefetch: true,
          subscribe: false,
        }),
      );
      startAuthTransition(() => {
        router.replace(redirectTo);
        router.refresh();
      });

      return true;
    },
    [dispatch, router, startAuthTransition],
  );

  const logoutAndRedirect = useCallback(
    (redirectTo?: string) => {
      void (async () => {
        await clearPersistedAuthSession();
        dispatch(logout());
        dispatch(baseApi.util.resetApiState());

        startAuthTransition(() => {
          if (redirectTo) {
            router.replace(redirectTo);
          }
          router.refresh();
        });
      })();
    },
    [dispatch, router, startAuthTransition],
  );

  const isDashboard = isDashboardRoute(pathname);
  const currentPath = pathname || "/";

  const shouldFetchProfile = isHydrated && isAuthenticated;
  const {
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    isError: isProfileError,
  } = useGetProfileQuery(undefined, {
    skip: !shouldFetchProfile,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!shouldFetchProfile) {
      return;
    }

    if (isProfileLoading || isProfileFetching) {
      return;
    }

    if (isProfileError) {
      logoutAndRedirect();
    }
  }, [
    isProfileError,
    isProfileFetching,
    isProfileLoading,
    logoutAndRedirect,
    shouldFetchProfile,
  ]);

  useEffect(() => {
    const unsubscribe = subscribeToAuthSync((event) => {
      if (event.source === getAuthSyncSourceId()) {
        return;
      }

      if (event.type === "logout") {
        dispatch(logout());
        dispatch(baseApi.util.resetApiState());
        router.refresh();
        return;
      }

      void (async () => {
        const session = await readPersistedAuthSession();
        if (session) {
          dispatch(hydrateFromStorage(session));
        } else {
          dispatch(logout());
          dispatch(baseApi.util.resetApiState());
        }

        router.refresh();
      })();
    });

    return unsubscribe;
  }, [dispatch, router]);

  useEffect(() => {
    if (!isDashboard || !isHydrated) {
      return;
    }

    if (!isAuthenticated) {
      router.replace(toLoginRedirect(currentPath));
    }
  }, [currentPath, isAuthenticated, isDashboard, isHydrated, router]);

  const contextValue = useMemo(
    () => ({
      accessToken,
      user,
      isAuthenticated,
      isHydrated,
      isAuthTransitionPending,
      navigateWithTransition,
      completeLogin,
      logoutAndRedirect,
    }),
    [
      accessToken,
      user,
      isAuthenticated,
      isHydrated,
      isAuthTransitionPending,
      navigateWithTransition,
      completeLogin,
      logoutAndRedirect,
    ],
  );

  const isProfilePending =
    shouldFetchProfile && !user && (isProfileLoading || isProfileFetching);
  const shouldBlockDashboard =
    isDashboard && (!isHydrated || !isAuthenticated || isProfilePending);
  const shouldShowOverlay = shouldBlockDashboard || isAuthTransitionPending;

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {shouldShowOverlay ? (
        <div className="bg-background fixed inset-0 z-120 flex items-center justify-center backdrop-blur-sm">
          <Spinner size="lg" />
        </div>
      ) : null}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside <AuthProvider />");
  }

  return context;
}
